import { store } from "redux/store";

//classes
import FlightPlan from "classes/flightPlan";
import Aircraft from "./aircraft";

//common components
import IconButtonWithTooltip from "components/common/IconButtonWithTooltip/IconButtonWithTooltip";
import SlidingTextButton from "components/common/SlidingTextButton/SlidingTextButton";

//assets
import routeAdd from "assets/icons/route-add-white.svg";
import tools from "assets/icons/tools-white.svg";

/**A class representing actions that can be performed on the aircraft*/
class AircraftActions {
	/**
	 * @param {string} _aircraftIdent A uuid of an aircraft.
	 */
	constructor(_aircraftIdent) {
		this.aircraftActions = {
			parked: [
				{
					textPl: "Przypisz plan lotu",
					textEn: "Assign flight plan",
					icon: <img src={routeAdd} alt="add" style={{ height: "1.35rem" }} />,
					onClick: this.assignAircraftToNewFlightPlan
				}
			],
			landed: [
				{
					textPl: "Odpraw",
					textEn: "Depart",
					icon: <i className="fas fa-plane-departure" style={{ color: "hsl(0, 100%, 100%)" }} />,
					preferedSize: "big",
					onClick: this.depart
				},
				{
					textPl: "Anuluj przypisanie samolotu",
					textEn: "Unassign aircraft",
					icon: <i className="fas fa-plane-circle-xmark" style={{ color: "hsl(0, 100%, 100%)" }} />,
					onClick: this.unassignAircraft
				}
			],
			delivery: [
				{
					textPl: "Dostarcz natychmiast",
					textEn: "Deliver now",
					icon: <i className="fas fa-angles-right" style={{ color: "inherit" }} />,
					onClick: () => null
				}
			],
			maintenance: [
				{
					textPl: "zakończ przegląd",
					textEn: "finish",
					icon: <img src={tools} alt="add" style={{ height: "1.35rem" }} />,
					onClick: () => null
				}
			],
			inflight: [],
			cleaning: [
				{
					textPl: "Anuluj przypisanie samolotu",
					textEn: "Unassign aircraft",
					icon: <i className="fas fa-plane-circle-xmark" style={{ color: "hsl(0, 100%, 100%)" }} />,
					onClick: this.unassignAircraft
				}
			]
		};

		this.aircraft = Aircraft.get(_aircraftIdent);
		if (!this.aircraft || !this.aircraft?.status) {
			this.actions = [];
			return this;
		}

		this.actions = this.aircraftActions[this.aircraft.status];
		if (this.aircraft.status !== "delivery") {
			this.actions.push({
				textPl: this.aircraft.grounded ? "Odziem samolot" : "Uziem samolot",
				textEn: this.aircraft.grounded ? "Unground aircraft" : "Ground aircraft",
				icon: this.aircraft.grounded ? (
					<i className="fas fa-plane-departure" style={{ color: "hsl(0, 100%, 100%)" }} />
				) : (
					<i className="fas fa-plane-slash" style={{ color: "hsl(0, 100%, 100%)" }} />
				),
				onClick: this.toggleGroundAircraft
			});
		}

		if (this.aircraft.grounded || this.aircraft.missingCrewCount > 0) {
			const indexOfDepart = this.actions.findIndex(a => a.textEn === "Depart");
			if (indexOfDepart > -1) this.actions[indexOfDepart].disabled = true;
		}
	}

	assignAircraftToNewFlightPlan = e => {
		e.stopPropagation();

		store.dispatch({ type: "SET_WINDOW_ADDRESS", state: "aircraftIcon.Routes.flight_plans.creator" });
		setTimeout(
			() =>
				document.dispatchEvent(
					new CustomEvent("setPlaneToAssignToFlightPlan", { detail: { aircraftIdent: this.aircraft.ident } })
				),
			60
		);
	};

	unassignAircraft = e => {
		e.stopPropagation();
		const currLang = store.getState().lang;
		const confirmationMsg =
			currLang === "PL"
				? "Czy na pewno chcesz usunąć samolot z planu lotu? Jeśli samolot nie jest na ziemi operacja nie powiedzie się!"
				: "Are you sure you want to remove the plane from the flight plan? If the aircraft isn't on the ground, the operation will fail!";
		const confirmation = window.confirm(confirmationMsg);
		if (!confirmation) return;

		const successes = FlightPlan.get(this.aircraft.assignedFlightPlan).unassignAircraft();
		if (!successes) alert(currLang === "PL" ? "Operacja nie powiodła się!" : "Operation failed!");
	};

	toggleGroundAircraft = e => {
		e.stopPropagation();
		if (this.aircraft.status === "delivery") return;
		this.aircraft.grounded = !this.aircraft.grounded;
		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this.aircraft });

		Aircraft.saveFleet();
	};

	depart = e => {
		e.stopPropagation();
		this.aircraft.depart();
	};

	getLanguage = (en, pl) => (store.getState().lang === "PL" ? pl : en);

	/**
	 * Returns buttons based on actions.
	 * @param {"light" | "dark"} _theme Light by default.
	 */
	getJsx(theme = "light", border = false, globalPreferedSize = "normal", ignore = []) {
		if (!this.aircraft) return [];

		let background;

		if (theme === "dark") {
			switch (this.aircraft.status) {
				case "landed":
					background = "darkGreen";
					break;
				case "delivery":
					background = "purple";
					break;
				case "cleaning":
					if (!ignore.includes("cleaning")) background = "orange";
					break;
				default:
					background = "darkBlue";
					break;
			}
		} else {
			switch (this.aircraft.status) {
				case "landed":
					background = "green";
					break;
				case "delivery":
					background = "pink";
					break;
				case "cleaning":
					background = "orange";
					break;
				default:
					background = "blue";
					break;
			}
		}

		if (this.aircraft.grounded && !ignore.includes("grounded")) background = "red";
		if (this.aircraft.status === "cleaning" && ignore.includes("cleaning")) {
			if (theme === "dark") {
				background = this.aircraft.assignedFlightPlan ? "darkGreen" : "darkBlue";
			} else {
				background = this.aircraft.assignedFlightPlan ? "green" : "blue";
			}
		}

		return this.actions.map(({ textPl, textEn, icon, onClick, preferedSize = "normal", disabled = false }, i, a) => {
			if (a.length > 2 && preferedSize !== "big" && globalPreferedSize !== "big") {
				return (
					<IconButtonWithTooltip
						text={this.getLanguage(textEn, textPl)}
						icon={icon}
						background={background}
						disabled={disabled}
						border={border}
						key={i}
						onClick={onClick}
					/>
				);
			}

			return (
				<SlidingTextButton
					text={this.getLanguage(textEn, textPl)}
					icon={icon}
					background={background}
					disabled={disabled}
					border={border}
					key={i}
					onClick={onClick}
				/>
			);
		});
	}
}

export default AircraftActions;
